import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Loader2, Check, X, ShieldCheck, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";

const ROLES = [
    { value: "patient", label: "Paciente", group: "Uso pessoal" },
    { value: "caregiver", label: "Cuidador(a)", group: "Uso pessoal" },
    { value: "doctor", label: "Médico(a)", group: "Profissional de saúde", license: "CRM" },
    { value: "nurse", label: "Enfermeiro(a)", group: "Profissional de saúde", license: "COREN" },
    { value: "physiotherapist", label: "Fisioterapeuta", group: "Profissional de saúde", license: "CREFITO" },
    { value: "occupational_therapist", label: "Terapeuta Ocupacional", group: "Profissional de saúde", license: "CREFITO" },
    { value: "nutritionist", label: "Nutricionista", group: "Profissional de saúde", license: "CRN" },
    { value: "psychologist", label: "Psicólogo(a)", group: "Profissional de saúde", license: "CRP" },
    { value: "social_worker", label: "Assistente Social", group: "Profissional de saúde", license: "CRESS" },
    { value: "pharmacist", label: "Farmacêutico(a)", group: "Profissional de saúde", license: "CRF" },
    { value: "transpersonal_therapist", label: "Terapeuta Transpessoal", group: "Profissional de saúde", license: "Registro" },
];

const PROFESSIONAL_ROLES = ROLES.filter((r) => r.group === "Profissional de saúde").map((r) => r.value);

// Password strength rules (must match backend regex)
const PW_RULES = [
    { id: "len", label: "Mínimo 8 caracteres", test: (s) => s.length >= 8 },
    { id: "upper", label: "Uma letra maiúscula", test: (s) => /[A-Z]/.test(s) },
    { id: "num", label: "Um número", test: (s) => /\d/.test(s) },
    { id: "sym", label: "Um caractere especial", test: (s) => /[^A-Za-z0-9]/.test(s) },
];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "patient", phone: "", crm: "", specialty: "" });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const currentRole = ROLES.find((r) => r.value === form.role) || ROLES[0];
    const isProfessional = PROFESSIONAL_ROLES.includes(form.role);

    const pwChecks = useMemo(() => PW_RULES.map((r) => ({ ...r, ok: r.test(form.password) })), [form.password]);
    const strength = pwChecks.filter((c) => c.ok).length;
    const strengthLabel = ["Muito fraca", "Fraca", "Razoável", "Boa", "Forte"][strength];
    const strengthColor = ["bg-[#E5E5E5]", "bg-[#F4A261]", "bg-[#E9C46A]", "bg-[#8DB255]", "bg-[#2A9D8F]"][strength];
    const canSubmit = strength === 4 && form.name.trim() && form.email.trim();

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (strength < 4) { setError("Senha não atende aos critérios de segurança."); return; }
        setLoading(true);
        try {
            const result = await register(form);
            // If pending approval, don't try to auth — show info and redirect to login
            if (result?.pending_approval || isProfessional) {
                toast.success("Cadastro enviado! Aguarde aprovação do administrador.");
                navigate("/login");
                return;
            }
            navigate("/dashboard");
        } catch (e) {
            setError(e.message);
        } finally { setLoading(false); }
    };

    const groupedRoles = ROLES.reduce((acc, r) => { (acc[r.group] ||= []).push(r); return acc; }, {});

    return (
        <div className="min-h-screen bg-base flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <span className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center">
                        <Stethoscope size={18} />
                    </span>
                    <span className="font-display text-brand text-lg font-semibold">Clínica Fernandes</span>
                </Link>
                <div className="card-earthy">
                    <h1 className="font-display text-2xl font-medium text-brand mb-1">Criar conta</h1>
                    <p className="text-sm text-muted-clinical mb-6">Preencha os dados para começar.</p>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="label-clinical">Nome completo</label>
                            <input data-testid={TID.auth.nameInput} required value={form.name} onChange={set("name")} className="input-clinical" />
                        </div>
                        <div>
                            <label className="label-clinical">Perfil</label>
                            <select data-testid={TID.auth.roleSelect} value={form.role} onChange={set("role")} className="input-clinical">
                                {Object.entries(groupedRoles).map(([g, list]) => (
                                    <optgroup key={g} label={g}>
                                        {list.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                        {isProfessional && (
                            <div data-testid="pro-approval-notice" className="text-xs bg-[#FBF4E5] text-[#8A6A15] border-l-4 border-[#EDD9A6] rounded p-3 flex gap-2">
                                <Info size={14} className="shrink-0 mt-0.5" />
                                <span>Cadastros de profissionais passam por <strong>aprovação do administrador</strong> antes de acessar o sistema.</span>
                            </div>
                        )}
                        <div>
                            <label className="label-clinical">E-mail</label>
                            <input data-testid={TID.auth.emailInput} required type="email" value={form.email} onChange={set("email")} className="input-clinical" />
                        </div>
                        <div>
                            <label className="label-clinical">Senha</label>
                            <input data-testid={TID.auth.passwordInput} required type="password" value={form.password} onChange={set("password")} className="input-clinical" placeholder="Mínimo 8 caracteres" />
                            {form.password && (
                                <>
                                    <div className="mt-2 h-1.5 rounded-full bg-alt overflow-hidden">
                                        <div data-testid="pw-strength-bar" className={`h-full transition-all ${strengthColor}`} style={{ width: `${(strength / 4) * 100}%` }} />
                                    </div>
                                    <div data-testid="pw-strength-label" className="text-[10px] uppercase tracking-wider mt-1 text-muted-clinical">{strengthLabel}</div>
                                    <ul className="mt-2 space-y-1 text-xs">
                                        {pwChecks.map((c) => (
                                            <li key={c.id} data-testid={`pw-rule-${c.id}`} className={`flex items-center gap-2 ${c.ok ? "text-[#2A5746]" : "text-muted-clinical"}`}>
                                                {c.ok ? <Check size={12} /> : <X size={12} />} {c.label}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                        <div>
                            <label className="label-clinical">Telefone (opcional)</label>
                            <input value={form.phone} onChange={set("phone")} className="input-clinical" />
                        </div>
                        {isProfessional && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-clinical">{currentRole.license || "Registro profissional"}</label>
                                    <input data-testid="reg-license" value={form.crm} onChange={set("crm")} className="input-clinical" placeholder={`Ex: ${currentRole.license === "Registro" ? "Associação" : currentRole.license + "-SP 12345"}`} />
                                </div>
                                <div>
                                    <label className="label-clinical">Especialidade</label>
                                    <input value={form.specialty} onChange={set("specialty")} className="input-clinical" />
                                </div>
                            </div>
                        )}
                        {error && (
                            <div data-testid={TID.auth.error} className="text-sm text-[var(--status-danger)] bg-[var(--status-danger-bg)] border-l-4 border-[var(--status-danger)] p-3 rounded">{error}</div>
                        )}
                        <button data-testid={TID.auth.submitBtn} disabled={loading || !canSubmit} className="btn-primary w-full">
                            {loading && <Loader2 size={16} className="animate-spin" />} <ShieldCheck size={14} /> Criar conta
                        </button>
                    </form>
                    <div className="mt-6 text-center text-sm text-muted-clinical">
                        Já tem conta? <Link data-testid={TID.auth.switchToLogin} to="/login" className="text-brand font-medium hover:underline">Entrar</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
